import { FC, ReactNode, useEffect, CSSProperties } from "react";
import { createPortal } from "react-dom";
import { CloseOutlined } from "@ant-design/icons";

import "./AutoModel.scss";

/** 面板最大宽度预设，对应原 Modal `width` 量级 */
export type AutoModelSize = "default" | "sm" | "md" | "lg" | "xl" | "xxl" | "full";

export interface AutoModelProps {
  /** 是否展示 */
  open: boolean;
  /** 关闭：右上角关闭、遮罩（可配置）、Esc（可配置） */
  onClose?: () => void;
  /** 标题（文案或任意节点） */
  title: ReactNode;
  /** 主内容区 */
  children?: ReactNode;
  /** 底部左侧内容（如积分消耗），可隐藏 */
  footerLeft?: ReactNode;
  /** 底部右侧：一般为「确认 / 保存」整颗按钮节点 */
  footerRight?: ReactNode;
  /** 是否展示右上角关闭，默认 true */
  closable?: boolean;
  /** 点击遮罩是否触发 onClose，默认 true */
  maskClosable?: boolean;
  /** 是否允许 Esc 关闭，默认 true（对齐 antd Modal） */
  keyboard?: boolean;
  /** 打开/关闭变化后回调（用于 uploadWorkspace 等布局量测；在下一帧触发以接近 antd afterOpenChange） */
  afterOpenChange?: (open: boolean) => void;
  /** 面板最大宽度预设 */
  size?: AutoModelSize;
  /** 覆盖面板 max-width（优先级高于 size），如 925、'90vw' */
  contentMaxWidth?: number | string;
  /** 面板额外样式（如原 Modal bodyStyle 中的 maxHeight） */
  contentStyle?: CSSProperties;
  /** 内容区外层 class（原 body 区域） */
  bodyClassName?: string;
  /** 内容区外层 style */
  bodyStyle?: CSSProperties;
  /** 遮罩层 class（可与 className 合并） */
  className?: string;
  overlayClassName?: string;
  /** 面板 class */
  contentClassName?: string;
}

const overlayClass = "auto-model-root auto-model-overlay";

const contentBaseClass = "auto-model-content";

const sizeToContentModifier: Record<AutoModelSize, string> = {
  default: "auto-model-content--default",
  sm: "auto-model-content--sm",
  md: "auto-model-content--md",
  lg: "auto-model-content--lg",
  xl: "auto-model-content--xl",
  xxl: "auto-model-content--xxl",
  full: "auto-model-content--full",
};

const headerClass = "auto-model__header";

const titleClass = "auto-model__title";

const closeBtnClass = "auto-model__close";

const bodyClass = "auto-model__body";

const footerClass = "auto-model__footer";

const footerSlotClass = "auto-model__footer-slot";

/**
 * 统一弹窗壳层（样式对齐设计稿）；业务状态与按钮逻辑由调用方传入，不在此组件内实现。
 */
export const AutoModel: FC<AutoModelProps> = ({
  open,
  onClose,
  title,
  children,
  footerLeft,
  footerRight,
  closable = true,
  maskClosable = true,
  keyboard = true,
  afterOpenChange,
  size = "default",
  contentMaxWidth,
  contentStyle,
  bodyClassName,
  bodyStyle,
  className,
  overlayClassName,
  contentClassName,
}) => {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (afterOpenChange == null) return;
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        afterOpenChange(open);
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [open, afterOpenChange]);

  useEffect(() => {
    if (!open || !keyboard || !onClose) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, keyboard, onClose]);

  if (!open) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!maskClosable || !onClose) return;
    if (e.target === e.currentTarget) onClose();
  };

  const sizeModifier = sizeToContentModifier[size] ?? sizeToContentModifier.default;
  const maxWidthStyle: CSSProperties | undefined =
    contentMaxWidth != null
      ? {
          maxWidth: typeof contentMaxWidth === "number" ? `${contentMaxWidth}px` : contentMaxWidth,
        }
      : undefined;

  const contentClasses = [contentBaseClass, sizeModifier, contentClassName].filter(Boolean).join(" ");

  const node = (
    <div
      className={[overlayClass, className, overlayClassName].filter(Boolean).join(" ")}
      role="presentation"
      onClick={handleOverlayClick}
    >
      <div
        className={contentClasses}
        style={{ ...contentStyle, ...maxWidthStyle }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={headerClass}>
          <div className={titleClass}>{title}</div>
          {closable && onClose ? (
            <button type="button" className={closeBtnClass} onClick={onClose} aria-label="关闭">
              <CloseOutlined style={{ fontSize: 18 }} />
            </button>
          ) : null}
        </div>

        {children != null && children !== false ? (
          <div className={[bodyClass, bodyClassName].filter(Boolean).join(" ")} style={bodyStyle}>
            {children}
          </div>
        ) : null}

        {footerLeft != null || footerRight != null ? (
          <div className={footerClass}>
            {footerLeft != null ? (
              <div className={`${footerSlotClass} auto-model__footer-slot--left`}>{footerLeft}</div>
            ) : null}
            {footerRight != null ? (
              <div className={`${footerSlotClass} auto-model__footer-slot--right`}>{footerRight}</div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(node, document.body);
};
